import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Send, CheckCircle2, MessageCircle, Clock, Shield } from "lucide-react";

export default function ContactScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user;

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("Please write a message of at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setSent(true);
      setMessage("");
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell section="legal" tabbar={isAuthed}>
      <button
        onClick={() => (isAuthed ? navigate(-1) : navigate("/login"))}
        className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline"
        data-testid="contact-back"
      >
        <ArrowLeft size={14} /> {isAuthed ? "Back" : "Back to sign in"}
      </button>

      <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-shadow-green">Contact us</h1>
      <p className="text-muted-soft text-xs uppercase tracking-wider mb-6">We&apos;d love to hear from you</p>

      {/* Header card */}
      <div className="glass-card-strong p-6 mb-5 fade-up">
        <div className="flex items-center gap-3 mb-4">
          <span
            className="inline-flex items-center justify-center w-12 h-12 rounded-full"
            style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}
          >
            <Mail size={20} className="text-[#4CAF50]" />
          </span>
          <h2 className="text-lg font-bold">Get in touch</h2>
        </div>
        <p className="text-soft text-sm leading-relaxed mb-4">
          Questions, bugs, partnership ideas, or just want to say hi about a plant you identified — drop us a note.
          We read every message and usually reply within 24 hours.
        </p>

        {/* Trust/value strip */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="text-center rounded-lg py-2.5 px-1"
               style={{ background: "rgba(76,175,80,0.10)", border: "1px solid rgba(76,175,80,0.22)" }}>
            <Clock size={14} className="text-[#4CAF50] mx-auto mb-1" />
            <p className="text-[10px] uppercase tracking-wider font-bold text-soft">Reply</p>
            <p className="text-[11px] text-white font-semibold">&lt; 24 h</p>
          </div>
          <div className="text-center rounded-lg py-2.5 px-1"
               style={{ background: "rgba(76,175,80,0.10)", border: "1px solid rgba(76,175,80,0.22)" }}>
            <MessageCircle size={14} className="text-[#4CAF50] mx-auto mb-1" />
            <p className="text-[10px] uppercase tracking-wider font-bold text-soft">Channel</p>
            <p className="text-[11px] text-white font-semibold">Email</p>
          </div>
          <div className="text-center rounded-lg py-2.5 px-1"
               style={{ background: "rgba(76,175,80,0.10)", border: "1px solid rgba(76,175,80,0.22)" }}>
            <Shield size={14} className="text-[#4CAF50] mx-auto mb-1" />
            <p className="text-[10px] uppercase tracking-wider font-bold text-soft">Privacy</p>
            <p className="text-[11px] text-white font-semibold">Encrypted</p>
          </div>
        </div>
      </div>

      {/* Form / Success */}
      <div
        className="rounded-2xl p-5 mb-5 fade-up stagger-1"
        style={{
          background: "rgba(8,12,18,0.74)",
          border: "1px solid rgba(76,175,80,0.32)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
        data-testid="contact-form-card"
      >
        {sent ? (
          <div className="text-center py-4" data-testid="contact-success">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ background: "rgba(76,175,80,0.20)", boxShadow: "var(--glow-green)" }}
            >
              <CheckCircle2 size={28} className="text-[#4CAF50]" />
            </div>
            <h3 className="text-lg font-bold text-white mb-1">Message sent!</h3>
            <p className="text-soft text-sm">
              Thanks {name ? <b className="text-white">{name.split(" ")[0]}</b> : ""}. We&apos;ll get back to you at{" "}
              <b className="text-white">{email}</b>.
            </p>
            <button
              onClick={() => setSent(false)}
              className="mt-4 text-[12px] text-[#A5D6A7] underline"
              data-testid="contact-send-another"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-soft">Name</label>
              <input
                type="text"
                required
                minLength={1}
                maxLength={120}
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="glass-input mt-1"
                data-testid="contact-name"
                disabled={busy}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-soft">Email</label>
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="glass-input mt-1"
                data-testid="contact-email"
                disabled={busy}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-soft">Message</label>
              <textarea
                required
                minLength={10}
                maxLength={5000}
                placeholder="How can we help? (minimum 10 characters)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="glass-input mt-1"
                style={{ resize: "vertical", minHeight: 150 }}
                data-testid="contact-message"
                disabled={busy}
              />
              <p className="text-[10px] text-muted-soft mt-1.5">
                {message.length}/5000
              </p>
            </div>

            <button
              type="submit"
              disabled={busy || message.trim().length < 10}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-bold transition-all disabled:opacity-50 hover:brightness-110"
              style={{
                background: "linear-gradient(135deg,#4CAF50,#66BB6A)",
                color: "#0a0a0a",
                boxShadow: "0 6px 16px rgba(76,175,80,0.35)",
                letterSpacing: "0.02em",
              }}
              data-testid="contact-submit"
            >
              <Send size={16} /> {busy ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>

      <p className="text-[11px] text-muted-soft text-center mt-2 mb-6">
        We&apos;ll reply within 24 hours.
      </p>
    </Shell>
  );
}
